/**
 * Pure planners for the conversation companion stack: a chat with the
 * artifacts it opened beside it, the newest expanded and older ones
 * collapsed into titles.
 */
import {
  calculateConversationCompanionFocusLayout,
  reconcileConversationCompanionFocusLayout,
  swapConversationCompanionExpandedFrame,
  type ConversationCompanionLayout,
} from "@/lib/conversation-companion-focus-geometry";
import type { FocusWindowBox } from "@/lib/focus-window-layout";
import type { PresentationState } from "@/lib/focus/focus-types";
import {
  planFocusBoxes,
  type FocusPlanEnv,
} from "@/lib/focus/focus-plan-layout";

function companionLayout(
  state: PresentationState | null,
): ConversationCompanionLayout | null {
  const layout = state?.customFocusLayout;
  return state?.variant === "focus" &&
    layout?.kind === "conversation_companion_stack"
    ? layout
    : null;
}

function planCompanionBoxes(
  state: PresentationState,
  activeId: string,
  layout: ConversationCompanionLayout,
  env: FocusPlanEnv,
): PresentationState | null {
  return planFocusBoxes(
    {
      ...state,
      activeIndex: Math.max(0, state.frameIds.indexOf(activeId)),
      customFocusLayout: layout,
    },
    activeId,
    { boxes: layout.boxes, preserveCustomFocusLayout: true },
    env,
  );
}

/** Expand a collapsed or overflowed artifact in the stack. */
export function planExpandCompanionFrame(
  state: PresentationState | null,
  frameId: string,
  env: FocusPlanEnv,
): PresentationState | null {
  const layout = companionLayout(state);
  if (
    !state ||
    !layout ||
    (!layout.collapsedFrameIds.includes(frameId) &&
      !layout.overflowHiddenFrameIds?.includes(frameId))
  ) {
    return null;
  }
  const swapped =
    layout.overflowTitleFrameId === frameId ||
    layout.overflowHiddenFrameIds?.includes(frameId)
      ? null
      : swapConversationCompanionExpandedFrame(layout, state.frameIds, frameId);
  const nextLayout =
    swapped ??
    reconcileConversationCompanionFocusLayout(
      layout,
      state.frameIds,
      state.frameIds,
      frameId,
    );
  return planCompanionBoxes(state, frameId, nextLayout, env);
}

/** Take an artifact out of the stack; the chat stays. */
export function planRemoveCompanionFrame(
  state: PresentationState | null,
  frameId: string,
  env: FocusPlanEnv,
): PresentationState | null {
  const layout = companionLayout(state);
  if (
    !state ||
    !layout ||
    frameId === layout.conversationFrameId ||
    !state.frameIds.includes(frameId)
  ) {
    return null;
  }
  const previousActiveId =
    state.frameIds[state.activeIndex] ?? layout.conversationFrameId;
  const frameIds = state.frameIds.filter((id) => id !== frameId);
  const activeId =
    previousActiveId === frameId
      ? layout.conversationFrameId
      : previousActiveId;
  const expandedId =
    layout.expandedStackFrameId !== frameId &&
    layout.expandedStackFrameId &&
    frameIds.includes(layout.expandedStackFrameId)
      ? layout.expandedStackFrameId
      : frameIds[frameIds.length - 1]!;
  const nextLayout = reconcileConversationCompanionFocusLayout(
    layout,
    state.frameIds,
    frameIds,
    expandedId,
  );
  return planCompanionBoxes({ ...state, frameIds }, activeId, nextLayout, env);
}

/** Keep a box the user dragged or resized an artifact to. */
export function planRememberCompanionBox(
  state: PresentationState | null,
  frameId: string,
  box: FocusWindowBox,
  env: FocusPlanEnv,
): PresentationState | null {
  const layout = companionLayout(state);
  const index = state?.frameIds.indexOf(frameId) ?? -1;
  if (!state || !layout || index < 0) return null;
  if (layout.collapsedFrameIds.includes(frameId)) return null;
  const replace = (boxes: FocusWindowBox[]) =>
    boxes.map((current, i) => (i === index ? box : current));
  return {
    ...state,
    boxes: replace(state.boxes),
    customFocusLayout: {
      ...layout,
      boxes: Object.assign([...layout.boxes], { [index]: box }),
      surface:
        layout.surface ??
        (state.focusWindow ? undefined : state.surface) ??
        env.surface,
      userAdjusted: true,
    },
  };
}

/** Frames and stack layout that open an artifact beside its chat. */
export function planArtifactBesideConversation(
  state: PresentationState | null,
  conversationFrameId: string,
  artifactFrameId: string,
  env: FocusPlanEnv,
): { frameIds: string[]; customFocusLayout: ConversationCompanionLayout } {
  const layout = companionLayout(state);
  const currentFrameIds =
    state && layout?.conversationFrameId === conversationFrameId
      ? state.frameIds.filter((id) => env.frameType(id) !== undefined)
      : [conversationFrameId];
  if (layout && currentFrameIds.includes(artifactFrameId)) {
    const swapped =
      layout.overflowTitleFrameId === artifactFrameId
        ? null
        : swapConversationCompanionExpandedFrame(
            layout,
            currentFrameIds,
            artifactFrameId,
          );
    return {
      frameIds: currentFrameIds,
      customFocusLayout:
        swapped ??
        reconcileConversationCompanionFocusLayout(
          layout,
          currentFrameIds,
          currentFrameIds,
          artifactFrameId,
        ),
    };
  }
  const frameIds = [...currentFrameIds, artifactFrameId];
  return {
    frameIds,
    customFocusLayout: layout
      ? reconcileConversationCompanionFocusLayout(
          layout,
          currentFrameIds,
          frameIds,
          artifactFrameId,
        )
      : calculateConversationCompanionFocusLayout(
          env.surface,
          frameIds,
          conversationFrameId,
          artifactFrameId,
        ),
  };
}
